import clsx from 'clsx';
import { FC, useId } from 'react';
import { cn } from '@provider-directory/functions/cn';
import { normalizeParagraphBreaks } from '@provider-directory/functions/text';

interface ProviderDetailListContent {
  title: string;
  content: string | string[];
}

interface ProviderTextContent {
  title: string;
  content: string[] | ProviderDetailListContent[];
}

interface ProviderTextSectionProps {
  id?: string;
  title: string;
  content: string | ProviderTextContent[];
  className?: string;
}

function renderHtmlContent(
  content: React.ReactNode,
  props?: React.HTMLAttributes<HTMLDivElement>,
): React.ReactNode {
  if (!content) return null;

  if (typeof content === 'string') {
    const html = normalizeParagraphBreaks(content);

    return (
      <div
        {...props}
        className={cn(
          // Spacing between paragraphs
          '[&>p:not(:last-child)]:mb-6',

          // Reset list styles and add consistent spacing
          '[&>ul]:ml-6 [&>ul]:list-disc',
          '[&>ol]:ml-6 [&>ol]:list-decimal',
          '[&>ul:not(:last-child)]:mb-6',
          '[&>ol:not(:last-child)]:mb-6',
          props?.className,
        )}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return content;
}

const ProviderTextSubsection: FC<ProviderTextContent> = ({
  title,
  content,
}) => {
  const isStringList = content.every((item) => typeof item === 'string');
  return (
    <div
      className={'flex flex-col gap-4'}
      data-testid={`provider-subsection-${title
        .replace(/\s+/g, '')
        .toLowerCase()}`}
    >
      <h3
        className={clsx(
          'font-serif text-gray-900',
          'pb-2 pt-2 text-heading-xlarge desktop:text-heading-2xlarge',
        )}
      >
        {title}
      </h3>
      {isStringList ? (
        <div
          className={
            'line-he flex flex-col gap-6 text-body-medium text-gray-900'
          }
        >
          {(content as string[]).length > 1 ? (
            <ul className={'ml-6 list-disc'}>
              {(content as string[]).map((item, index) => (
                <li
                  key={`${item}-${index}`}
                  itemProp={'availableService'}
                  itemScope={true}
                  itemType={'https://schema.org/MedicalProcedure'}
                >
                  {renderHtmlContent(item, { itemProp: 'name' })}
                </li>
              ))}
            </ul>
          ) : (
            <div
              itemProp={'availableService'}
              itemScope={true}
              itemType={'https://schema.org/MedicalProcedure'}
            >
              {renderHtmlContent((content as string[])[0], {
                itemProp: 'name',
              })}
            </div>
          )}
        </div>
      ) : (
        <dl className={'flex flex-col gap-6'}>
          {(content as ProviderDetailListContent[]).map((item) => (
            <div key={item.title} className={'flex flex-col'}>
              <dt className={'pb-2 text-body-medium-bold'}>{item.title}</dt>
              <dd
                className={
                  'line-he flex flex-col gap-6 text-body-medium text-gray-900'
                }
              >
                {Array.isArray(item.content) && item.content.length > 1 ? (
                  <ul className={'ml-6 list-disc'}>
                    {item.content.map((subItem, subIndex) => (
                      <li key={subIndex}>{renderHtmlContent(subItem)}</li>
                    ))}
                  </ul>
                ) : (
                  renderHtmlContent(item.content)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

const ProviderTextSection: FC<ProviderTextSectionProps> = ({
  id,
  title,
  content,
  className,
}) => {
  const isString = typeof content === 'string';
  const headingId = useId();
  return content.length === 0 ? null : (
    <section
      aria-labelledby={headingId}
      className={'flex flex-col gap-4 pb-6'}
      id={id}
      data-testid={`provider-section-${title
        .replace(/\s+/g, '')
        .toLowerCase()}`}
    >
      <h2
        id={headingId}
        className={clsx(
          'font-serif text-gray-900',
          'pb-4 text-heading-2xlarge desktop:text-[28px] desktop:text-heading-3xlarge',
        )}
      >
        {title}
      </h2>
      {isString ? (
        <div
          className={cn('line-he text-body-medium text-gray-900', className)}
        >
          {renderHtmlContent(content)}
        </div>
      ) : (
        <div className={'flex flex-col gap-6'}>
          {content.map((item) => (
            <ProviderTextSubsection key={item.title} {...item} />
          ))}
        </div>
      )}
    </section>
  );
};
export default ProviderTextSection;
